// סיכום AI card (mock s4, working column top) — the AI summary of the latest
// transcribed call for this contact (enrichment/transcriptAndSummary of the
// newest answered call on the user's virtual numbers). Renders instantly from
// sessionCache; graceful absence when no call was ever transcribed.
//
// ---------------------------------------------------------------------------
// MOBILE — why this card carries its own chrome (read before touching the sx).
//
// This is the ONE TabCard on the whole customer page that paints a background.
// TabCard's < sm branch is deliberately CHROME-LESS — a flat
// `Box { py: 2; border-bottom: 1px }`: no radius, no side borders, no
// horizontal padding — because it was designed for the ~20 TRANSPARENT panels
// that read as a divider-separated list. For those it is invisible; for a
// TINTED one it means the fill renders as a square-cornered, border-less
// rectangle whose left/right edges are hard cuts at the viewport.
//
// A first attempt at "the AI card looks broken on mobile" added only the
// horizontal `px` — and was rejected again, correctly. Measured (Chrome,
// 320/375/414) that state was: computed border-radius 0px, border widths
// 0/0/1px/0, and a content column 32px NARROWER than every other section on
// the page (375px: 32→343 vs 16→359 everywhere else) with no visible edge to
// justify the inset. That is precisely "seems to have a min width and is cut".
// Nothing overflows and nothing sets a min-width — measured scrollWidth ==
// clientWidth at every width; the defect was never a width, it was a SURFACE.
//
// So the card now paints its own edge at BOTH breakpoints — 1px tinted border
// + radius, matching OverviewTaskStrip's StripShell, which sits directly above
// it in the mobile stack and has always read correctly. The inset content is
// then normal card padding rather than an unexplained indent.
//
// Do NOT push this chrome down into TabCard: that mobile branch is shared by
// every tab and must stay chrome-less for the transparent panels.
//
// The header is split into a fixed title + a wrapping meta caption for the same
// reason: as one long "סיכום AI · שיחה אחרונה · <when> · <duration>" string in
// a nowrap row it wrapped raggedly around the icon at 320px and squeezed the
// icon itself (SvgIcon is a flex item and shrinks unless told not to).
import { Box, Button, Skeleton, Stack, Typography } from "@material-ui/core";
import { AutoAwesome } from "@material-ui/icons";
import { alpha, useTheme } from "@material-ui/core/styles";
import useSettings from "hooks/useSettings";
import { fToNow } from "utils/formatTime";
import { TabCard } from "../../TabCard";
import { useContactSurfaceDown } from "../../contactSurface";
import { useSessionCacheValue } from "../../data/sessionCache";
import {
  latestAiCallCacheKey,
  fetchLatestTranscribedCall,
  LATEST_AI_CALL_MAX_AGE_MS
} from "./overviewData";
import { useSwitchTab } from "./useSwitchTab";
import type { LatestAiCall } from "./overviewData";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";

type OverviewAiSummaryCardProps = {
  contact: RealEstateContact;
};

// "4:12" from a seconds string (mock format) — hours only when relevant.
const formatCallDuration = (durationSeconds: string | undefined): string => {
  const total = parseInt(durationSeconds ?? "", 10);
  if (!Number.isFinite(total) || total <= 0) return "";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const paddedSeconds = String(seconds).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
};

export function OverviewAiSummaryCard({ contact }: OverviewAiSummaryCardProps): JSX.Element {
  const theme = useTheme();
  const switchTab = useSwitchTab();
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";
  /**
   * The SURFACE, because `TabCard` — which this card's sx is tuned against — already asks
   * the surface. Left on viewport breakpoints these two disagreed inside the CRM side
   * panel: TabCard took its flat mobile branch while every `{ xs, sm }` below resolved to
   * the `sm` value, so the padding, the radius and the bottom margin were all set for a
   * layout that was not the one being rendered.
   */
  const isNarrow = useContactSurfaceDown("sm");

  // data: undefined == not fetched yet (skeleton); null == no transcribed call.
  const { data, error } = useSessionCacheValue<LatestAiCall | null>(
    latestAiCallCacheKey(contact.id),
    () => fetchLatestTranscribedCall(contact.id),
    { maxAgeMs: LATEST_AI_CALL_MAX_AGE_MS }
  );

  // App-wide preference order (timeline/calls/transcription dialog all do
  // `AI?.callSummary ?? summary`): the parsed AI callSummary first, the raw
  // pipeline summary as fallback. `AI` is typed required but may be missing
  // at runtime (fetch accepts `summary || AI`).
  const summaryText =
    data?.transcript.AI?.callSummary?.trim() || data?.transcript.summary?.trim() || "";
  const duration = formatCallDuration(data?.call.duration);
  const when = data?.call.createdTime ? fToNow(data.call.createdTime, isRTL) : "";
  // Everything after the title, as ONE wrappable unit — so a narrow viewport
  // drops the whole "שיחה אחרונה · לפני 3 שעות · 4:12" line under the title
  // instead of breaking it mid-phrase around the icon.
  const metaLine = ["שיחה אחרונה", when, duration].filter(Boolean).join(" · ");

  // ONE `border` shorthand with the colour baked in, NOT `border` + a separate
  // `borderColor`. TabCard spreads the caller sx into an object that ALREADY
  // declares `borderColor`, and an object spread keeps a duplicated key at its
  // FIRST position — so a caller `borderColor` would be emitted BEFORE a caller
  // `border`, and the shorthand would then reset the colour to `currentColor`.
  const edge = `1px solid ${alpha(theme.palette.info.main, 0.24)}`;

  return (
    <TabCard
      sx={{
        // The tinted fill needs a real EDGE at both breakpoints — see the
        // header note. `border` (shorthand, spread last by TabCard) beats the
        // mobile branch's `borderBottom: 1` on all four sides; at >= sm the
        // Card has no border of its own, so this is the first time the
        // long-intended tinted outline actually renders there.
        px: isNarrow ? 2 : 2.5,
        py: isNarrow ? 2 : 2.5,
        // narrow 1.5 == StripShell's radius (the strip directly above it);
        // wide 2 == shape.borderRadiusMd, i.e. exactly what MuiCard's override
        // applies, so on desktop it keeps sibling-card geometry.
        borderRadius: isNarrow ? 1.5 : 2,
        border: edge,
        // Mobile stack is gap-less (TabCard draws dividers), so a real card in
        // it has to space itself off the next flat section.
        mb: isNarrow ? 1 : 0,
        bgcolor: alpha(theme.palette.info.main, 0.08),
        // TabCard's mobile branch declares `'&:last-child': { borderBottom: 0 }`
        // — higher specificity than the base class, and the spread REPLACES it
        // wholesale rather than merging. Restating the edge here keeps the
        // bottom border if this card ever lands last (it is 2nd of 6 today, but
        // the order is data-dependent).
        "&:last-child": { border: edge }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ gap: 0.75, rowGap: 0.25, mb: 1, flexWrap: "wrap" }}
      >
        {/* flexShrink:0 — an SvgIcon is a flex item like any other and was
            measurably squeezed (18px → 17.4px) once the header wrapped. */}
        <AutoAwesome sx={{ fontSize: 18, color: "info.dark", flexShrink: 0 }} />
        <Typography variant="subtitle2" sx={{ color: "info.dark", flexShrink: 0 }}>
          סיכום AI
        </Typography>
        {/* nowrap + ellipsis is what makes the meta move to the next line AS A
            UNIT (a wrappable item would instead break mid-phrase and leave a
            lone "4:12" hanging under the title), with truncation as the
            worst-case guard so it can never widen the card. */}
        <Typography
          variant="caption"
          sx={{
            color: "info.dark",
            opacity: 0.8,
            minWidth: 0,
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {metaLine}
        </Typography>
      </Stack>

      {data ? (
        <>
          {/* overflowWrap:anywhere — summaries are free AI text; a long unbroken
              token (URL/digits) must break instead of forcing the card wider
              than narrow mobile viewports (TabCard has no clipping on mobile). */}
          <Typography
            variant="body2"
            dir="auto"
            sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {summaryText || "אין טקסט סיכום לשיחה הזו."}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="text" color="info" onClick={() => switchTab("calls")}>
              הקלטה + תמלול ←
            </Button>
          </Box>
        </>
      ) : data === undefined && !error ? (
        <Stack sx={{ gap: 0.5 }}>
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="75%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          אין עדיין שיחה מתומללת ללקוח — סיכום ה-AI של השיחה הבאה יופיע כאן.
        </Typography>
      )}
    </TabCard>
  );
}
